import { ERROR, LOADING, READY, useVisualState } from '../../../hooks/useVisualState'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { get, post } from '../../../helpers/request'

import Button from '../../../components/Button/Button'
import FileUploader from '../../../components/FileUploader/FileUploader'
import { Form } from '../../../components/Form/Form'
import Modal from '../../../components/Modal/Modal'
import ModalContent from '../../../components/Modal/ModalContent'
import ModalFooter from '../../../components/Modal/ModalFooter'
import ModalHeader from '../../../components/Modal/ModalHeader'
import ProgressBar from '../../../components/ProgressBar/ProgressBar'
import Select from '../../../components/Select/Select'
import SelectItem from '../../../components/Select/SelectItem'
import TagsInput from '../../../components/TagsInput/TagsInput'
import TextField from '../../../components/TextField/TextField'
import { closeEvent } from '../../../components/Modal/ModalEvents'
import t from '../../../helpers/i18n'
import { uploadFileHelper } from '../../../helpers/upload'
import useObjectState from '../../../hooks/useObjectState'
import { useParams } from 'react-router-dom'

const FileFormModal = ({ onCloseRequested }) => {
  const [[fileState, file], setFile] = useVisualState()
  const [formValid, setFormValid] = useState(false)
  const [uploading, setUploading] = useObjectState()
  const editFileData = useRef()
  const pendingFileData = useRef()
  const cancel = useRef(false)
  const uploadModal = useRef()
  const { siteId, siteFileId } = useParams()

  const handleSave = useCallback(
    (target) => {
      uploadFileHelper(
        pendingFileData.current,
        { file: 'files' },
        setUploading
      ).then((data) => {
        if (!cancel.current) {
          const url =
            '/soccer/sites/' +
            siteId +
            '/files' +
            (siteFileId !== 'create' ? '/' + siteFileId : '')
          post(url, { ...editFileData.current, ...data })
            .then(() => {
              closeEvent(target, true)
            })
            .catch((error) => {
              // TODO: better error handling
              console.log(error)
            })
        }
      })
    },
    [siteId, siteFileId, setUploading]
  )

  const handleCancelUpload = useCallback(() => {
    cancel.current = true
    closeEvent(uploadModal.current, false)
  }, [])

  useEffect(() => {
    if (siteFileId && siteFileId !== 'create') {
      setFile(LOADING)
      get('/soccer/sites/' + siteId + '/files/' + siteFileId)
        .then((response) => {
          setFile(READY, response)
        })
        .catch((error) => {
          setFile(ERROR, error)
        })
    } else {
      setFile(READY, {})
    }
  }, [siteId, siteFileId, setFile])

  return (
    <div>
      {fileState === READY && (
        <Modal onCloseRequested={onCloseRequested}>
          <ModalHeader
            title={
              siteFileId !== 'create' ? t('modal_headers.Edit_file') : t('modal_headers.Add_file')
            }
          />
          <ModalContent>
            <Form
              data={editFileData}
              pending={pendingFileData}
              onValidityChange={setFormValid}
            >
              <TextField
                name='title'
                label={t('labels.Title')}
                initialValue={file.title}
                maxLength={100}
                minLength={1}
                required
              />
              <TextField
                name='description'
                label={t('labels.Description')}
                initialValue={file.description}
                maxLength={140}
              />
              <TagsInput
                name='tags'
                label={t('labels.Tags')}
                initialValue={file.tags}
                placeholder={t('placeholders.type_a_new_tag')}
              />
              <FileUploader
                name='file'
                label={t('labels.File')}
                filename={file.filename}
                source={
                  file.file &&
                  process.env.REACT_APP_IMAGE_BASE_URL +
                    '/files/' +
                    file.file +
                    '?download'
                }
              />
              <Select
                name='status'
                label={t('labels.File_status')}
                initialValue={file.status}
              >
                <SelectItem value='private'>{t('select_item.Private')}</SelectItem>
                <SelectItem value='published'>{t('select_item.Published')}</SelectItem>
                <SelectItem value='deleted'>{t('select_item.Deleted_(WARNING)')}</SelectItem>
              </Select>
            </Form>
          </ModalContent>
          <ModalFooter>
            <div>
              <Button
                onClick={(event) => closeEvent(event.currentTarget)}
                label={t('buttons.Cancel')}
              />
            </div>
            <div>
              <Button
                disabled={!formValid}
                onClick={(event) => handleSave(event.currentTarget)}
                label={t('buttons.Save')}
              />
            </div>
          </ModalFooter>
          {uploading && (
            <Modal
              elementRef={uploadModal}
              onCloseRequested={handleCancelUpload}
            >
              <ModalHeader title={t('modal_headers.Uploading_files')} back={false} />
              <ModalContent dialog>
                {Object.keys(uploading).map((key) => (
                  <div key={key}>
                    <div className='text-label color-text'>
                      {pendingFileData.current[key].value.name}
                    </div>
                    <ProgressBar percent={uploading[key]} />
                  </div>
                ))}
              </ModalContent>
            </Modal>
          )}
        </Modal>
      )}
    </div>
  )
}

export default FileFormModal
